import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MaterialModule } from '../../material.module';
import { CommonTableService } from '../../services/common-table.service';
import { CommonService } from '../../services/common.service';
import {
  dateTimeFormat,
  popupButtons,
  ModalPopupMessages,
  manifestLinkedBagsKeys,
  defaultPrintWidth,
  conditionCheckConstants,
  entityPrintKeys,
} from '../../utility/constants';
import { PrintService } from '../../services/print.service';
import {
  isPrintPayloadMessage,
  startPrintPreviewReadyNotifier,
} from '../../utility/print-layout.util';

interface ArPrintColumn {
  key: string;
  label: string;
}

// Same column widths as the node AR invoice PDF, so print and PDF wrap the same way.
const AR_PRINT_COLUMN_WIDTHS_PT: Record<string, number> = {
  shipmentDate: 54,
  ibcReference: 92,
  shipperReference: 82,
  consignee: 118,
  pieces: 30,
  weight: 42,
  item: 30,
  charges: 67,
};
const AR_CHARGE_BLOCK_KEYS = new Set(['item', 'charges']);
const AR_RIGHT_ALIGNED_KEYS = new Set(['pieces', 'weight', 'charges']);
const AR_NO_WRAP_KEYS = new Set(['shipmentDate', 'pieces', 'weight']);
const AR_USD_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

@Component({
  selector: 'app-information-page-print',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './information-page-print.component.html',
  styleUrl: './information-page-print.component.scss',
})
export class InformationPagePrintComponent implements OnInit, OnDestroy {
  private printPayloadReceived = false;
  private stopReadyNotifier?: () => void;
  public dataSources: { [key: string]: any } = {};
  public displayedColumns: { [key: string]: string[] } = {};
  public summaryCustomiseTableColumns: { [key: string]: any } = {};
  public isFrom!: string;
  public dateFormat = dateTimeFormat.onlyDate;
  public pageSize = '';
  public popupButtons = popupButtons;
  public isForprint = false;
  public isInfoPagePrintClicked = false;
  public isInfoPagePrintWarning = false;
  public warningMessage = ModalPopupMessages.printWarningContent;
  public objectKeys = Object.keys;
  public infoPageData: any;

  public bagsLinkedData: any[] = [];
  /** PAC-24667: optional nested blocks; empty keeps the existing output. */
  public manifestsLinkedData: any[] = [];
  public linkedShipmentsData: any[] = [];
  public nestedBagsTitle = '';
  public entityPrintKeys = entityPrintKeys;
  private readonly BAGS_SECTION_KEY = manifestLinkedBagsKeys.manifestsBags;
  public bagLinkedKeys = manifestLinkedBagsKeys;
  public defaultPrintWidth = defaultPrintWidth;
  private selectedBagCols: string[] = [];
  private selectedShipmentCols: string[] = [];
  private selectedCommodityCols: string[] = [];
  /** One entry per invoice; the by-date AR page prints several, each on its own page. */
  public arInvoicePrints: any[] = [];
  public arChargeColumns: ArPrintColumn[] = [];
  public arShipmentColumns: ArPrintColumn[] = [];
  public arChargeBlockColumns: ArPrintColumn[] = [];
  public arColumnWidthsPt = AR_PRINT_COLUMN_WIDTHS_PT;
  public arRightAlignedKeys = AR_RIGHT_ALIGNED_KEYS;
  public arNoWrapKeys = AR_NO_WRAP_KEYS;
  private readonly document = inject(DOCUMENT);
  private readonly titleService = inject(Title);
  private arPageStyle?: HTMLStyleElement;

  constructor(
    public commonTableService: CommonTableService,
    public commonService: CommonService,
    public printService: PrintService
  ) {
    window.addEventListener('message', this.messageHandler);
  }

  ngOnInit(): void {
    this.stopReadyNotifier = startPrintPreviewReadyNotifier(
      () => this.printPayloadReceived
    );
  }

  ngOnDestroy() {
    this.stopReadyNotifier?.();
    this.arPageStyle?.remove();
    window.removeEventListener('message', this.messageHandler);
  }

    messageHandler = (event: any) => {
      // Only the info-page print payload from the opener; ignore any other message.
      if (!isPrintPayloadMessage(event, ['selectedColumns'])) return;
      const data = event.data;
      if (Object.keys(event.data).length) {
        this.printPayloadReceived = true;
        this.stopReadyNotifier?.();
        this.infoPageData = event.data.selectedColumns;

        this.resetDisplayColumns();
        this.getDisplayColumns();
        this.pageSize = event.data.selectedPageSize;
        this.isForprint = event.data.isPrintDialogRequired;
        this.isInfoPagePrintWarning = event.data.isPreviewWarning;
        const rawBagsData = event.data.bagsLinkedData || [];
        this.bagsLinkedData = this.transformBagsDataForPrint(rawBagsData);
        this.nestedBagsTitle = data.nestedBagsTitle ?? '';
        this.manifestsLinkedData = Array.isArray(data.manifestsLinkedData)
          ? data.manifestsLinkedData.map((manifest: any) => ({
              ...manifest,
              bags: this.transformBagsDataForPrint(manifest?.bags ?? []),
            }))
          : [];
        this.linkedShipmentsData = this.transformShipmentsForPrint(
          data.linkedShipmentsData ?? []
        );
        this.selectedBagCols = data.selectedBagCols ?? [];
        this.selectedShipmentCols = data.selectedShipmentCols ?? [];
        this.selectedCommodityCols = data.selectedCommodityCols ?? [];
        this.setArInvoicePrintData(data.arInvoicePrint);
        if (this.isForprint) {
          this.print();
        }
      }
    };

  private transformBagsDataForPrint(bagsData: any[]): any[] {
    if (!bagsData || !Array.isArray(bagsData)) {
      return [];
    }
    return bagsData.map((bag: any) => {
      const shipmentDetails = bag.shipmentDetails || bag.shipments || [];
      return {
        ...bag,
        bagExt: bag.bagExt || bag.bagNumber || '',
        weight: bag.weight || bag.bagWeight || '',
        shipmentDetails: this.transformShipmentsForPrint(shipmentDetails),
      };
    });
  }

  private transformShipmentsForPrint(shipmentDetails: any): any[] {
    if (!Array.isArray(shipmentDetails)) {
      return [];
    }
    return shipmentDetails.map((shipment: any) => {
      let commodityDetails: any[] = [];
      if (Array.isArray(shipment.commodityDetails)) {
        commodityDetails = shipment.commodityDetails;
      } else if (shipment.commodityInfo) {
        commodityDetails = [shipment.commodityInfo];
      }

      return {
        ...shipment,
        shipmentExt: shipment.shipmentExt || shipment.shipmentNumber || '',
        origin: shipment.origin || shipment.originName || '',
        destination: shipment.destination || shipment.destinationName || '',
        weight: shipment.weight || shipment.shipmentWeight || '',
        shipperDetails: shipment.shipperDetails || shipment.shipper || {},
        consigneeDetails: shipment.consigneeDetails || shipment.consignee || {},
        commodityDetails: commodityDetails,
      };
    });
  }

  /** Any shipment-level column selected in the print popup. */
  public get hasShipmentColumns(): boolean {
    return (
      this.showShipmentExt ||
      this.showOrigin ||
      this.showDestination ||
      this.showShipmentWeight ||
      this.showShipper ||
      this.showConsignee ||
      this.showCommodityColumn
    );
  }

  private setArInvoicePrintData(arInvoicePrint: any) {
    const invoices = (
      Array.isArray(arInvoicePrint) ? arInvoicePrint : [arInvoicePrint]
    ).filter(Boolean);
    this.arChargeColumns = invoices.length
      ? this.summaryCustomiseTableColumns['arCharges'] ?? []
      : [];
    this.arShipmentColumns = this.arChargeColumns.filter(
      col => !AR_CHARGE_BLOCK_KEYS.has(col.key)
    );
    this.arChargeBlockColumns = this.arChargeColumns.filter(col =>
      AR_CHARGE_BLOCK_KEYS.has(col.key)
    );
    this.arInvoicePrints = invoices.map((invoice: any) => {
      const shipments = invoice.shipmentDetails ?? [];
      const sumOf = (field: string) =>
        shipments.reduce(
          (sum: number, shipment: any) => sum + (Number(shipment[field]) || 0),
          0
        );
      return {
        ...invoice,
        totalDue: sumOf('total'),
        totalPieces: shipments.reduce(
          (sum: number, shipment: any) => sum + (Number(shipment.pieces) || 1),
          0
        ),
        totalWeight: sumOf('weight').toFixed(1),
      };
    });
    if (invoices.length) {
      this.applyArInvoicePrintSetup();
    }
  }

  // Injected at runtime: a component-level @page would also apply to non-AR prints.
  private applyArInvoicePrintSetup(): void {
    const invoiceNumbers = this.arInvoicePrints
      .map(invoice => invoice.invoiceNumber)
      .join(', ');
    this.titleService.setTitle(`AR Invoice - ${invoiceNumbers}`);
    this.arPageStyle?.remove();
    this.arPageStyle = this.document.createElement('style');
    this.arPageStyle.textContent =
      '@page { margin: 60pt 40pt; }' +
      '@media print { .main { overflow: visible !important; } .infoPage-Preview-Window { margin: 0 !important; } .print-page { padding: 0 !important; } }';
    this.document.head.appendChild(this.arPageStyle);
  }

  public formatArAmount(amount: unknown): string {
    const value = Number(String(amount ?? '').replace(/[$,\s]/g, ''));
    return AR_USD_FORMAT.format(Number.isFinite(value) ? value : 0);
  }

  public print() {
    this.isInfoPagePrintClicked = true;
    window.onafterprint = () => {
      window.close();
    };
    // Defer one tick so the view renders, then wait for images (logo) to load;
    // printing immediately captures the page before the logo has loaded.
    setTimeout(() => {
      this.waitForImagesToLoad().then(() => window.print());
    }, 0);
  }

  private waitForImagesToLoad(): Promise<void> {
    const pending = Array.from(document.images).filter(img => !img.complete);
    if (!pending.length) {
      return Promise.resolve();
    }
    return Promise.all(
      pending.map(
        img =>
          new Promise<void>(resolve => {
            // Resolve on error too so a broken image never blocks printing.
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          })
      )
    ).then(() => undefined);
  }

  /** A re-used preview window must not keep sections from a previous payload. */
  private resetDisplayColumns() {
    this.dataSources = {};
    this.displayedColumns = {};
    this.summaryCustomiseTableColumns = {};
  }

  private getDisplayColumns() {
    for (const key in this.infoPageData) {
      if (Object.prototype.hasOwnProperty.call(this.infoPageData, key)) {
        if (this.infoPageData?.[key].data) {
          this.dataSources[key] = this.infoPageData[key].data;
          this.displayedColumns[key] = this.infoPageData[key].headerKeys || [];
          this.summaryCustomiseTableColumns[key] =
            this.infoPageData[key].config || [];
        }
      }
    }
  }
  
   showNumberRangeSubheader(sectionKey:string): boolean {
     return this.summaryCustomiseTableColumns?.[sectionKey]?.some((b:any) => b.key === 'setRange');
  }
  // beg linked
  public isFieldSelected(sectionKey: string, fieldKey: string): boolean {
    const list: string[] = this.displayedColumns?.[sectionKey] || [];
    return list.includes(fieldKey);
  }
  public get showBag(): boolean {
    return this.isBagFieldSelected(conditionCheckConstants.bagExt);
  }
  public get showBagWeight(): boolean {
    return (
       this.isBagFieldSelected(conditionCheckConstants.bagWeight) ||
    this.isBagFieldSelected(conditionCheckConstants.weight)
    );
  }
  public get showShipmentExt(): boolean {
    return this.isBagFieldSelected(conditionCheckConstants.shipmentExt);
  }
  public get showOrigin(): boolean {
  return this.isBagFieldSelected(conditionCheckConstants.origin);
}

public get showDestination(): boolean {
  return this.isBagFieldSelected(conditionCheckConstants.destination);
}

public get showShipmentWeight(): boolean {
  return (
    this.isBagFieldSelected(conditionCheckConstants.shipmentWeight) ||
    this.isBagFieldSelected(conditionCheckConstants.weight)
  );
}

public get showShipper(): boolean {
  return (
    this.isBagFieldSelected(conditionCheckConstants.shipperInfo) ||
    this.isBagFieldSelected(conditionCheckConstants.shipperDetails)
  );
}

public get showConsignee(): boolean {
  return (
    this.isBagFieldSelected(conditionCheckConstants.consigneeInfo) ||
    this.isBagFieldSelected(conditionCheckConstants.consigneeDetails)
  );
}


public get showCommodityColumn(): boolean {
  return (
    this.selectedCommodityCols.includes('commodityDetails') ||
    this.selectedCommodityCols.includes('hsCode') ||
    this.selectedCommodityCols.includes('commodityValue') ||
    this.selectedCommodityCols.includes('commodityDescription')
  );


}
 private isBagFieldSelected(fieldKey: string): boolean {

  const bagCols = this.selectedBagCols ?? [];
  const shipmentCols = this.selectedShipmentCols ?? [];
  const commodityCols = this.selectedCommodityCols ?? [];

  return (
    bagCols.includes(fieldKey) ||
    shipmentCols.includes(fieldKey) ||
    commodityCols.includes(fieldKey)
  );
}

public getPartyName(party: any): string {
  if (!party) return '';
  return (
    party.shipperName ||
    party.name ||
    party.companyName ||
    party.comapnyName ||
    ''
  );
}

public getPartyCompany(party: any): string {
  if (!party) return '';
  return party.companyName || party.comapnyName || '';
}

public getPartyAddress(party: any): string {
  return party?.address || '';
}
public skipNestedBagSections(sectionKey: string): boolean {
    return ![
      this.BAGS_SECTION_KEY,
      'shipmentInformation',
      'bagInformation',
      'shipmentInfo',
      'commodityDetails',
      'linkedShipmentInfo',
      'arInvoiceInfo',
      'arCharges'
    ].includes(sectionKey);
  }

public getCommodityFieldNumber(fieldKey: string): number {
    const order = ['hsCode', 'commodityValue', 'commodityDescription'];
    let number = 1;
    for (const key of order) {
      if (key === fieldKey) {
        return number;
      }
      if (this.selectedCommodityCols.includes(key)) {
        number++;
      }
    }
    return number;
  }
  
  public shouldShowCommodityField(fieldKey: string): boolean {
    return this.selectedCommodityCols.includes(fieldKey);
  }
}
